import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';

const StoryCard = ({ story, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const currentRef = cardRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.2 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [delay]);

  const hasLink = Boolean(story.link);
  const Wrapper = hasLink ? 'a' : 'div';
  const wrapperProps = hasLink 
    ? { href: story.link, target: '_blank', rel: 'noopener noreferrer' } 
    : {};

  return (
    <div
      ref={cardRef}
      data-testid={`story-card-${story.id || story._id}`}
      className={`transition-all duration-700 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <Wrapper 
        {...wrapperProps}
        className={`block relative overflow-hidden bg-black border border-white/10 hover:border-[#FF4600]/50 transition-all duration-300 group h-full ${
          hasLink ? 'cursor-pointer' : ''
        }`}
      >
        <div className="relative h-72 overflow-hidden">
          <img
            src={story.image}
            alt={story.title}
            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110 grayscale group-hover:grayscale-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute top-4 left-4">
            <span className="bg-[#FF4600] text-white text-xs font-bold uppercase tracking-widest px-3 py-1">
              {story.category}
            </span>
          </div>
          {hasLink && (
            <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-sm p-2 group-hover:bg-[#FF4600] transition-colors">
              <ArrowUpRight className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
          )}
        </div>
        <div className="p-8">
          <div className="flex items-center gap-2 text-xs text-[#FF4600] mb-4 uppercase tracking-widest font-bold">
            <span>{story.date}</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-black mb-4 text-white uppercase tracking-tight group-hover:text-[#FF4600] transition-colors leading-tight">
            {story.title}
          </h3>
          <p className="text-white/60 leading-relaxed text-base font-light">
            {story.description}
          </p>
          {hasLink && (
            <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/40 group-hover:text-[#FF4600] transition-colors">
              <span>Mehr erfahren</span>
              <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
            </div>
          )}
        </div>
      </Wrapper>
    </div>
  );
};

export default StoryCard;
